/** Tick/step input helpers and contract lookup for futures close-position rows. */

export function decNum(v) {
    if (v == null || v === "") return NaN;
    if (typeof v === "object" && v !== null && "$numberDecimal" in v) {
        const n = parseFloat(v.$numberDecimal);
        return Number.isFinite(n) ? n : NaN;
    }
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : NaN;
}

function decStr(v) {
    if (v == null || v === "") return "";
    if (typeof v === "object" && v !== null && "$numberDecimal" in v) {
        return String(v.$numberDecimal ?? "");
    }
    return String(v);
}

function decimalPlaces(step) {
    const s = decStr(step);
    if (!s.includes(".")) return 0;
    const trimmed = s.replace(/0+$/, "").replace(/\.$/, "");
    const dot = trimmed.indexOf(".");
    return dot >= 0 ? trimmed.length - dot - 1 : 0;
}

export function floorToIncrement(value, increment) {
    const v = decNum(value);
    const inc = decNum(increment);
    if (!Number.isFinite(v) || v <= 0) return 0;
    if (!Number.isFinite(inc) || inc <= 0) return v;
    const floored = Math.floor(v / inc + 1e-12) * inc;
    const dp = decimalPlaces(increment);
    return dp > 0 ? parseFloat(floored.toFixed(dp)) : floored;
}

export function formatOrderDecimal(value, increment) {
    const n = decNum(value);
    if (!Number.isFinite(n)) return "";
    const dp = Math.max(decimalPlaces(increment), 0);
    if (dp === 0) {
        return String(Math.trunc(n));
    }
    const fixed = n.toFixed(dp);
    return fixed.replace(/(\.[0-9]*?)0+$/, "$1").replace(/\.$/, "") || "0";
}

export function getIncrementDecimalPlaces(increment) {
    return Math.max(decimalPlaces(increment), 0);
}

export function resolveIncrementSize(propValue, contract, contractField, fallback = "0.01") {
    const direct = decStr(propValue);
    if (direct && decNum(direct) > 0) return direct;
    const fromContract = decStr(contract?.[contractField]);
    if (fromContract && decNum(fromContract) > 0) return fromContract;
    return fallback;
}

export function sanitizeIncrementInput(raw, increment) {
    let s = String(raw ?? "").replace(/[^\d.]/g, "");
    const maxDp = getIncrementDecimalPlaces(increment);

    const dotIdx = s.indexOf(".");
    if (dotIdx >= 0) {
        const intPart = s.slice(0, dotIdx);
        const fracPart = s.slice(dotIdx + 1).replace(/\./g, "");
        if (maxDp === 0) return intPart;
        s = `${intPart}.${fracPart.slice(0, maxDp)}`;
    }

    return s;
}

export function snapToIncrementInput(raw, increment) {
    const trimmed = String(raw ?? "").trim();
    if (!trimmed) return "";
    const floored = floorToIncrement(trimmed, increment);
    if (floored <= 0) return "";
    return formatOrderDecimal(floored, increment);
}

/** Snap qty to step_size and cap at position size. */
export function snapAndCapCloseQty(raw, stepSize, maxQty) {
    const snapped = snapToIncrementInput(raw, stepSize);
    if (!snapped) return "";
    const n = decNum(snapped);
    const max = decNum(maxQty);
    if (Number.isFinite(max) && max > 0 && Number.isFinite(n) && n > max + 1e-12) {
        return snapToIncrementInput(String(max), stepSize);
    }
    return snapped;
}

export function findFuturesContractInPairs(pairs, symbol) {
    if (!symbol || !Array.isArray(pairs)) return null;
    const sym = String(symbol).toUpperCase();
    return (
        pairs.find(
            (p) =>
                String(p?.symbol ?? "").toUpperCase() === sym ||
                String(p?.base_currency_id ?? "").toUpperCase() === sym
        ) ?? null
    );
}

/** Default close row: mark price (tick) + full position size (step, capped). */
export function buildDefaultFuturesCloseInputs(position, contract) {
    const tickSize = resolveIncrementSize(null, contract, "tick_size", "0.01");
    const stepSize = resolveIncrementSize(null, contract, "step_size", "0.001");
    const mark = decNum(position?.mark_price);
    const avg = decNum(position?.average_entry_price ?? position?.entry_price);
    const priceSource = mark > 0 ? mark : avg;
    const price =
        priceSource > 0 ? snapToIncrementInput(String(priceSource), tickSize) : "";
    const posQty = decNum(position?.quantity);
    const qty = posQty > 0 ? snapAndCapCloseQty(String(posQty), stepSize, posQty) : "";
    return { price, qty, tickSize, stepSize };
}
